import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { assessmentService } from '../../api';
import {
    Plus, ClipboardCheck, LayoutGrid, List,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import ListPageHeader from '../../components/list/ListPageHeader';
import ListToolbar from '../../components/list/ListToolbar';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import { ShortCardGrid, ListSection } from '../../components/list/ShortCard';
import { AdminAssessmentShortCard } from '../../components/list/ShortCardVariants';
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';

function AdminAssessments() {
    const [assessments, setAssessments] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [viewMode, setViewMode] = useState('grid');

    const fetchAssessments = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                page_size: searchQuery ? 100 : pageSize,
            };
            if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
            if (typeFilter !== 'all') params.assessment_type = typeFilter;

            const res = await assessmentService.getAssessments(params);
            const { results, count } = parsePaginated(res);
            setAssessments(results);
            setTotalCount(searchQuery ? results.length : count);
        } catch (err) {
            toast.error('Failed to load assessments');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, typeFilter, page, pageSize, searchQuery]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, typeFilter, searchQuery]);

    useEffect(() => {
        fetchAssessments();
    }, [fetchAssessments]);

    const filteredAssessments = assessments.filter((a) =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase())
        || a.course_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const displayCount = searchQuery ? filteredAssessments.length : totalCount;
    const pagedDisplay = searchQuery
        ? filteredAssessments.slice((page - 1) * pageSize, page * pageSize)
        : filteredAssessments;

    const statusSegment = (
        <div className="ux-segment">
            {['all', 'active', 'inactive'].map((status) => (
                <button
                    key={status}
                    type="button"
                    className={statusFilter === status ? 'is-active' : ''}
                    onClick={() => setStatusFilter(status)}
                >
                    {status}
                </button>
            ))}
        </div>
    );

    const typeSegment = (
        <div className="ux-segment">
            {['all', 'exam', 'test', 'quiz', 'assignment'].map((type) => (
                <button
                    key={type}
                    type="button"
                    className={typeFilter === type ? 'is-active' : ''}
                    onClick={() => setTypeFilter(type)}
                >
                    {type === 'all' ? 'All types' : type}
                </button>
            ))}
        </div>
    );

    const viewToggle = (
        <div className="ux-list-view-toggle">
            <button type="button" className={viewMode === 'grid' ? 'is-active' : ''} onClick={() => setViewMode('grid')} aria-label="Grid view">
                <LayoutGrid size={18} />
            </button>
            <button type="button" className={viewMode === 'list' ? 'is-active' : ''} onClick={() => setViewMode('list')} aria-label="List view">
                <List size={18} />
            </button>
        </div>
    );

    return (
        <DashboardLayout>
            <main className="page-container content-width ux-list-page">
                <ListPageHeader
                    kicker="Assessment Management"
                    kickerIcon={ClipboardCheck}
                    title="Available Assessments"
                    description="Create and manage curriculum evaluations for your campus."
                    actions={(
                        <Link to="/admin/assessments/new" className="ux-btn-sm ux-btn-primary" style={{ padding: '0.65rem 1.25rem', fontSize: '0.875rem' }}>
                            <Plus size={18} />
                            Create Assessment
                        </Link>
                    )}
                />

                <ListToolbar
                    searchValue={searchQuery}
                    onSearchChange={setSearchQuery}
                    searchPlaceholder="Search by title or course…"
                    filters={(
                        <>
                            {statusSegment}
                            {typeSegment}
                            {viewToggle}
                        </>
                    )}
                />

                {loading ? (
                    <ListLoadingState label="Loading assessments…" />
                ) : pagedDisplay.length === 0 ? (
                    <ListEmptyState
                        icon={ClipboardCheck}
                        title="No assessments found"
                        description="Try adjusting filters or create a new assessment."
                    />
                ) : viewMode === 'grid' ? (
                    <ListSection title="Assessment library" description="Visual catalog of evaluations across your courses.">
                        <ShortCardGrid>
                            {pagedDisplay.map((assessment, index) => (
                                <AdminAssessmentShortCard key={assessment.id} assessment={assessment} index={index} />
                            ))}
                        </ShortCardGrid>
                    </ListSection>
                ) : (
                    <DataRowList>
                        {pagedDisplay.map((assessment, index) => (
                            <DataRow
                                key={assessment.id}
                                index={index}
                                leading={<div className="ux-row-thumb"><ClipboardCheck size={20} /></div>}
                                title={assessment.title}
                                subtitle={assessment.course_name || 'No course'}
                                badges={(
                                    <>
                                        <StatusPill status={assessment.assessment_type} label={assessment.assessment_type} />
                                        {!assessment.is_active && <StatusPill status="inactive" label="Inactive" />}
                                    </>
                                )}
                                actions={(
                                    <>
                                        <Link to={`/admin/assessments/${assessment.id}`} className="ux-btn-sm ux-btn-ghost" style={{ textDecoration: 'none' }}>Details</Link>
                                        <Link to={`/admin/assessments/${assessment.id}/questions`} className="ux-btn-sm ux-btn-primary" style={{ textDecoration: 'none' }}><Plus size={14} /> Questions</Link>
                                    </>
                                )}
                            />
                        ))}
                    </DataRowList>
                )}

                {!loading && pagedDisplay.length > 0 && (
                    <ListPagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={displayCount}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                    />
                )}
            </main>
        </DashboardLayout>
    );
}

export default AdminAssessments;
